import { useState } from 'react';
import { useCatIds, cat_id2lbl } from '../../lib/cat';

interface Props {
  data: any;
  brandProfile: any;
  onChange: (field: string, value: any) => void;
  onNext: () => void;
}

export default function CampaignBasicInfo({ data, brandProfile, onChange, onNext }: Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { ids: cat_ids } = useCatIds();

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!data.name.trim()) {
      newErrors.name = 'Campaign name is required';
    }

    if (!data.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!data.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Basic Information
        </h3>
        <p className="text-gray-600">
          Tell us about your campaign
        </p>
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Campaign Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="e.g., Summer Sale 2025"
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name}</p>
        )}
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Describe your campaign objectives and what makes it unique..."
          rows={4}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.description && (
          <p className="text-red-500 text-sm mt-1">{errors.description}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {data.description.length} characters
        </p>
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          value={data.category}
          onChange={(e) => onChange('category', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.category ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select a category</option>
          {cat_ids.map((id) => (
            <option key={id} value={id}>
              {cat_id2lbl(id)}
            </option>
          ))}
        </select>
        {errors.category && (
          <p className="text-red-500 text-sm mt-1">{errors.category}</p>
        )}
        {brandProfile?.industry && data.category === brandProfile.industry && (
          <p className="text-blue-600 text-xs mt-1 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
            </svg>
            Default from your profile
          </p>
        )}
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Landing Page <span className="text-gray-400 text-xs">(Optional)</span>
        </label>
        <input
          type="url"
          value={data.landing_page || ''}
          onChange={(e) => onChange('landing_page', e.target.value)}
          placeholder="https://example.com/campaign"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">
          Where should viewers be directed after seeing your ad?
        </p>
      </div>

      {}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-1">Campaign Tips</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Use a clear, descriptive name that reflects your campaign goal</li>
              <li>• Choose the category that best matches your target audience</li>
              <li>• Add a landing page to track conversions and engagement</li>
            </ul>
          </div>
        </div>
      </div>

      {}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
        >
          Continue to Assets →
        </button>
      </div>
    </div>
  );
}